import { ApiClient } from './ApiClient';
import { Message, Chat, MessageResponse, SendMessageOptions } from '@/types';

export class ChatService {
    private static instance: ChatService;
    private apiClient: ApiClient;
    private currentSessionId: string | null = null;

    private constructor() {
        this.apiClient = ApiClient.getInstance();
        // Generate a new session ID for this conversation
        this.startNewSession();
    }

    static getInstance(): ChatService {
        if (!ChatService.instance) {
            ChatService.instance = new ChatService();
        }
        return ChatService.instance;
    }

    private generateSessionId(): string {
        // Generate a UUID-like session ID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    public startNewSession(): void {
        this.currentSessionId = this.generateSessionId();
        console.log('New chat session started:', this.currentSessionId);
    }

    public getSessionId(): string {
        if (!this.currentSessionId) {
            this.startNewSession();
        }
        return this.currentSessionId!;
    }

    async sendMessage(
        messages: Message[],
        assistantId: string,
        options?: SendMessageOptions
    ): Promise<MessageResponse> {
        return this.apiClient.post('/chat', {
            messages,
            assistant: assistantId,
            sessionId: this.getSessionId(),
            ...options,
        });
    }

    async streamMessage(
        messages: Message[],
        assistantId: string,
        onChunk: (chunk: string) => void,
        onToolStatus?: (tool: string, status: string) => void
    ): Promise<void> {
        // If this is the first message (new conversation), generate new session
        if (messages.length === 1) {
            this.startNewSession();
        }

        return this.apiClient.stream('/v2/chat/stream', {
            messages,
            assistantId: assistantId,
            sessionId: this.getSessionId(),
        }, onChunk, undefined, onToolStatus);
    }

    async getChatHistory(userId?: string): Promise<Chat[]> {
        const endpoint = userId ? `/chats?userId=${userId}` : '/chats';
        return this.apiClient.get(endpoint);
    }

    async getChat(chatId: string): Promise<Chat> {
        return this.apiClient.get(`/chats/${chatId}`);
    }

    async deleteChat(chatId: string): Promise<void> {
        return this.apiClient.delete(`/chats/${chatId}`);
    }

    async updateChat(chatId: string, updates: Partial<Chat>): Promise<Chat> {
        return this.apiClient.put(`/chats/${chatId}`, updates);
    }
}